/** Subscription tier determines storage quota and feature access. */
export type SubscriptionTier = "unsubscribed" | "basic" | "pro";

/** Storage limits per subscription tier (in bytes). */
export const TIER_STORAGE_LIMITS: Record<SubscriptionTier, number> = {
  unsubscribed: 0,
  basic: 64 * 1024 * 1024, // 64 MB
  pro: 512 * 1024 * 1024, // 512 MB
};

/** Human-readable labels for each subscription tier. */
export const TIER_LABELS: Record<SubscriptionTier, string> = {
  unsubscribed: "Unsubscribed",
  basic: "Basic",
  pro: "Pro",
};

/** Full account profile response including subscription. */
export interface AccountProfileResponse {
  id: string;
  name: string;
  email: string;
  image: string | null;
  tier: SubscriptionTier;
  storageUsed: number;
  storageLimit: number;
  createdAt: string;
}

/** Request body for updating profile. */
export interface UpdateProfileRequest {
  name: string;
}

/** Request body for changing subscription tier. */
export interface UpdateSubscriptionRequest {
  tier: SubscriptionTier;
}

/** Response from subscription change. */
export interface UpdateSubscriptionResponse {
  success: boolean;
  tier: SubscriptionTier;
  /** True if the change was blocked or warned because usage exceeds the new limit. */
  overQuota?: boolean;
  usedBytes?: number;
  newLimitBytes?: number;
}

/** Response from the storage usage endpoint. */
export interface StorageUsageResponse {
  usedBytes: number;
  limitBytes: number;
  tier: SubscriptionTier;
}

/** Response from the quota check endpoint. */
export interface QuotaCheckResponse {
  allowed: boolean;
  usedBytes: number;
  limitBytes: number;
  tier: SubscriptionTier;
}

/** The kinds of library items. */
export type LibraryItemType =
  | "scene"
  | "object"
  | "collection"
  | "blend"
  | "pose"
  | "hand_pose"
  | "character"
  | "reference_image"
  | "material";

/** Item types that are reusable assets (models, collections, blend files, characters, materials). */
export const ASSET_TYPES: ReadonlySet<LibraryItemType> = new Set([
  "object",
  "collection",
  "blend",
  "character",
  "pose",
  "hand_pose",
  "material",
]);

/** Virtual folder sentinel for the Assets view. */
export const ASSETS_FOLDER = "__assets__" as const;

/** Virtual folder sentinel for the Hidden items view. */
export const HIDDEN_FOLDER = "__hidden__" as const;

/** Virtual folder sentinel for the References view. */
export const REFERENCES_FOLDER = "__references__" as const;

/** JSON metadata stored in library_items.metadata column. */
export interface LibraryItemMetadata {
  vertexCount?: number;
  objectCount?: number;
  objectNames?: string[];
  sourceFormat?: string;
  fileSize?: number;
}

/** API response shape for a folder. */
export interface FolderResponse {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
}

/** API response shape for a library item. */
export interface LibraryItemResponse {
  id: string;
  folderId: string | null;
  type: LibraryItemType;
  name: string;
  thumbnailUrl: string | null;
  fileUrl: string | null;
  sourceFileUrl: string | null;
  sourceHash: string | null;
  hidden: boolean;
  fileSize: number;
  metadata: LibraryItemMetadata | null;
  createdAt: string;
  updatedAt: string;
}

/** Standard API error response. */
export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

/** Request body for creating a library item. */
export interface CreateLibraryItemRequest {
  name: string;
  type: LibraryItemType;
  folderId?: string | null;
  fileKey: string;
  thumbnailKey?: string;
  sourceFileKey?: string;
  sourceHash?: string;
  fileSize?: number;
  metadata?: LibraryItemMetadata;
}

/** Request body for updating a library item. */
export interface UpdateLibraryItemRequest {
  name?: string;
  folderId?: string | null;
  fileKey?: string;
  thumbnailKey?: string;
  sourceFileKey?: string;
  hidden?: boolean;
  sourceHash?: string;
  fileSize?: number;
  metadata?: LibraryItemMetadata;
}

/** Request body for bulk operations. */
export interface BulkActionRequest {
  action: "move" | "delete" | "duplicate" | "hide" | "unhide";
  ids: string[];
  folderId?: string | null;
}

/** Request body for presigned URL generation. */
export interface PresignRequest {
  key: string;
  contentType: string;
}

/** Response from presigned URL generation. */
export interface PresignResponse {
  url: string;
  key: string;
}
